import React, { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdPopup.css';
import { MyContext, getImgUrl, uploadsPublicPath } from '../../../../Context/MyContext';
import AppIcon from '../../../../Components/AppIcon';

const STORAGE_KEY = 'og_ad_closed_time';

const AdPopup = () => {
  const { adsData } = useContext(MyContext);
  const navigate = useNavigate();

  // ✅ handle array or object; public API returns only active ads
  const ads = Array.isArray(adsData) ? adsData[0] : adsData;

  const [showAd, setShowAd] = useState(false);
  const [progress, setProgress] = useState(0);

  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const startRef = useRef(null);

  // ── Show after duration (cooldown respected) ──
  useEffect(() => {
    if (!ads || ads.is_active === false) return;

    const lastClosed = localStorage.getItem(STORAGE_KEY);

    if (lastClosed) {
      const diff = Date.now() - parseInt(lastClosed, 10);
      if (diff < (ads.cooldown_hours || 0) * 60 * 60 * 1000) return;
    }

    timerRef.current = setTimeout(() => {
      setShowAd(true);
    }, ads.duration || 3000);

    return () => clearTimeout(timerRef.current);
  }, [ads]);

  // ── Progress + auto close ──
  useEffect(() => {
    if (!showAd || !ads) return;

    startRef.current = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min((elapsed / (ads.auto_close || 10000)) * 100, 100);

      setProgress(pct);

      if (pct < 100) {
        progressRef.current = requestAnimationFrame(tick);
      } else {
        handleClose();
      }
    };

    progressRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(progressRef.current);
  }, [showAd, ads]);

  const handleClose = () => {
    cancelAnimationFrame(progressRef.current);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShowAd(false);
  };

  const handleClick = () => {
    handleClose();
    const raw = ads?.property_id;
    const pid = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
    if (Number.isFinite(pid) && pid > 0) {
      navigate(`/property/${pid}`);
      return;
    }
    if (ads?.cta_url) navigate(ads.cta_url);
  };

  // ── safety render
  if (!showAd || !ads || ads.is_active === false) return null;

  // ── SVG progress ──
  const R = 18;
  const CIRCUMFERENCE = 2 * Math.PI * R;
  const strokeDash = CIRCUMFERENCE - (progress / 100) * CIRCUMFERENCE;

  return (
    <div className="ad-overlay" onClick={handleClose}>
      <div className="ad-box" onClick={(e) => e.stopPropagation()}>

        {/* Close button */}
        <button className="ad-close-btn" onClick={handleClose} aria-label="Close ad">
          <svg className="ad-progress-ring" width="40" height="40" viewBox="0 0 40 40">
            <circle
              cx="20"
              cy="20"
              r={R}
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="3"
            />
            <circle
              cx="20"
              cy="20"
              r={R}
              fill="none"
              stroke="#2e7d32"
              strokeWidth="3"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={strokeDash}
              strokeLinecap="round"
              transform="rotate(-90 20 20)"
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <span className="ad-close-x"><AppIcon name="close" size={16} /></span>
        </button>

        {/* Ad Image */}
        <div className="ad-img-wrap" onClick={handleClick}>
          <img
            src={getImgUrl(uploadsPublicPath(ads.image))}
            alt="Advertisement"
            className="ad-img"
            onError={(e) => {
              e.target.src = '/fallback.jpg';
            }}
          />
        {ads.title || ads.cta_label ? (
          <div className="ad-img-overlay">
            {ads.title ? <p className="ad-title">{ads.title}</p> : null}
            {ads.cta_label ? <span className="ad-cta">{ads.cta_label} →</span> : null}
          </div>
        ) : null}
        </div>

      </div>
    </div>
  );
};

export default AdPopup;